import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  viewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import Chart from "chart.js/auto";
import type { ChartConfiguration } from "chart.js";
import { Widget } from "../../models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { SharedModule } from "../../../../shared/shared.module";
import { UiFacadeService } from "../../../user_infos/services/store/ui-facade.service";
import { parseNumber } from "../../../../shared/utils/number-utils.service";
import { AutoSignificantDigitsPipe } from "../../../../shared/pipes/number-sig.pipe";
import { LoaderSkeletonComponent } from "../../../../shared/loader-skeleton/loader-skeleton.component";
import { Travail } from "../../../info-journaliere/models/travail.model";
import { TranslateUtils } from "../../../../shared/utils/translate.utils";

const DEFAULT_TYPE_LABELS: Record<string, string> = {
  OF: "ij.type_of",
  CONGES: "ij.type_conges",
  RTT: "ij.type_rtt",
  MALADIE: "ij.type_maladie",
  AUTRE: "ij.type_autres",
};

@Component({
  selector: "vl-widget-repartition-travail-user-infos",
  standalone: true,
  imports: [CommonModule, WidgetTitleComponent, SharedModule, AutoSignificantDigitsPipe, LoaderSkeletonComponent],
  templateUrl: "./widget-repartition-travail-user-infos.component.html",
  styleUrls: ["./widget-repartition-travail-user-infos.component.scss"],
  host: { class: "widget-component" },
})
export class WidgetRepartitionTravailUserInfosComponent {
  widget = input<Widget>();
  metadata = input<EntityMetadata>();
  editable = input(true);

  private uiFacade = inject(UiFacadeService);
  stats = this.uiFacade.getState().stats;

  canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("repartitionChart");
  chart?: Chart<"doughnut", number[], string>;

  private parseDureeToMinutes(s: unknown): number {
    if (!s) return 0;
    const str = String(s).trim();
    let m = /^(\d{1,3})\s*h\s*(\d{1,2})?$/i.exec(str);
    if (m) return parseNumber(m[1] || 0) * 60 + parseNumber(m[2] || 0);
    m = /^(\d{1,3}):(\d{1,2})$/.exec(str);
    if (m) return parseNumber(m[1] || 0) * 60 + parseNumber(m[2] || 0);
    m = /^(\d+(?:[.,]\d+)?)$/.exec(str);
    if (m) return Math.round(parseNumber(m[1].replace(",", ".")) * 60);
    return 0;
  }

  chartData = computed(() => {
    const travail: Travail[] = this.stats()?.travail ?? [];
    if (!travail.length) return { labels: [], values: [], colors: [] };

    const grouped = new Map<string, { label: string; minutes: number }>();
    for (const t of travail) {
      const raw = (t as any).type?.denomination || t.defaultType || "AUTRE";
      const label = DEFAULT_TYPE_LABELS[raw]
        ? TranslateUtils.translate(DEFAULT_TYPE_LABELS[raw])
        : raw;
      const entry = grouped.get(label) ?? { label, minutes: 0 };
      entry.minutes += this.parseDureeToMinutes(t.duree);
      grouped.set(label, entry);
    }

    const entries = [...grouped.values()]
      .sort((a, b) => b.minutes - a.minutes)
      .slice(0, 12);

    const colors = this.makeColors(entries.length);

    return {
      labels: entries.map((e) => e.label),
      values: entries.map((e) => e.minutes),
      colors,
    };
  });

  legendLabels = computed(() => {
    const data = this.chartData();
    if (!data.labels.length) return null;
    const total = data.values.reduce((a, b) => a + b, 0);
    return data.labels.map((lbl, i) => ({
      label: lbl,
      percentage: total > 0 ? ((data.values[i] / total) * 100).toFixed(0) : "0",
      color: data.colors[i] ?? "#999",
    }));
  });

  private makeColors(n: number): string[] {
    const base = [210, 140, 260, 35, 170, 0, 195, 310];
    const out: string[] = [];
    for (let i = 0; i < n; i++) {
      const h =
        base[i % base.length] +
        (i >= base.length ? 12 * Math.floor(i / base.length) : 0);
      out.push(`hsl(${h % 360} 75% 55%)`);
    }
    return out;
  }

  constructor() {
    effect(() => {
      const canvas = this.canvasRef();
      const data = this.chartData();
      if (!canvas || !data.labels.length) {
        this.destroyChart();
        return;
      }
      this.buildChart(data);
    });
  }

  private destroyChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  private buildChart(data: { labels: string[]; values: number[]; colors: string[] }) {
    const canvas = this.canvasRef();
    if (!canvas) return;
    const ctx = canvas.nativeElement.getContext("2d");
    if (!ctx) return;
    this.destroyChart();

    const formatMinutes = (min: number) => {
      const h = Math.floor(min / 60);
      const mm = String(Math.round(min % 60)).padStart(2, "0");
      return `${h}h${mm}`;
    };

    const config: ChartConfiguration<"doughnut", number[], string> = {
      type: "doughnut",
      data: {
        labels: data.labels,
        datasets: [
          {
            label: TranslateUtils.translate("data.duree"),
            data: data.values,
            backgroundColor: data.colors,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => ` ${ctx.label}: ${formatMinutes(ctx.parsed)}`,
            },
          },
        },
      },
    };
    this.chart = new Chart<"doughnut", number[], string>(ctx, config);
  }
}
